import type {
  PharosRealtimeNotification,
  RealtimeConfig,
  RealtimeConnectionState,
  RealtimeNotificationHandler,
} from '@klareospace/pharos-lib-types';
import type { AxiosInstance } from 'axios';
import { EventDispatcher } from './event-dispatcher';

type ConnectionStateHandler = (state: RealtimeConnectionState, error?: Error) => void;

interface NegotiateResult {
  groups: string[];
  url: string;
}

/**
 * Credential interface matching @azure/web-pubsub-client v1 WebPubSubClientCredential.
 * The callback form causes the SDK to invoke getClientAccessUrl on every (re)connect
 * via _startCore(), ensuring a fresh Azure Web PubSub token is fetched each time.
 */
interface WebPubSubClientCredential {
  getClientAccessUrl: string | (() => Promise<string>);
}

/** Minimal interface for the Azure Web PubSub client instance methods used by this service. */
interface WebPubSubClientInstance {
  joinGroup: (group: string) => Promise<unknown>;
  on: (event: string, handler: (msg: unknown) => void) => void;
  start: () => Promise<void>;
  stop: () => void;
}

/** Constructor signature for the dynamically-imported Azure Web PubSub client. */
interface WebPubSubClientConstructor {
  new (clientAccessUrl: string): WebPubSubClientInstance;
  new (credential: WebPubSubClientCredential): WebPubSubClientInstance;
}

interface SubscribeOptions {
  event?: string | string[];
  handler: RealtimeNotificationHandler;
  operation?: string | string[];
  resource?: string | string[];
}

const DEFAULT_MAX_RECONNECT_ATTEMPTS = 5;
const DEFAULT_RECONNECT_INTERVAL_MS = 5_000;
const DEFAULT_HEARTBEAT_INTERVAL_MS = 30_000;

export class WebPubSubService {
  private apiInstance: AxiosInstance | null = null;
  private cachedGroups: string[] = [];
  private client: WebPubSubClientInstance | null = null;
  private config: RealtimeConfig | null = null;
  private connectionStateHandlers: Set<ConnectionStateHandler> = new Set();
  private dispatcher = new EventDispatcher();
  private error: Error | null = null;
  private heartbeatTimer?: ReturnType<typeof setInterval>;
  private reconnectAttempts = 0;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private state: RealtimeConnectionState = 'disconnected';
  private tenantId: string | null = null;
  private userId: string | null = null;

  getState(): RealtimeConnectionState {
    return this.state;
  }

  getError(): Error | null {
    return this.error;
  }

  /**
   * Connect to Azure Web PubSub.
   *
   * The client is built using the WebPubSubClientCredential callback form so the SDK
   * obtains a fresh negotiated token on the initial connect AND on every auto-reconnect
   * (SDK autoReconnect defaults to true and calls getClientAccessUrl via _startCore on
   * each attempt). This eliminates token-expiry reconnection failures.
   */
  async connect(config: RealtimeConfig, apiInstance: AxiosInstance, tenantId: string, userId: string): Promise<void> {
    this.clearReconnectTimer();
    this.apiInstance = apiInstance;
    this.config = config;
    this.tenantId = tenantId;
    this.userId = userId;
    this.setState('connecting');

    let WebPubSubClient: WebPubSubClientConstructor;

    try {
      const module = (await import('@azure/web-pubsub-client')) as { WebPubSubClient: WebPubSubClientConstructor };
      WebPubSubClient = module.WebPubSubClient;
    } catch {
      const message = '@azure/web-pubsub-client is not installed. Install it to enable realtime features.';
      this.setState('error', new Error(message));
      return;
    }

    try {
      this.dispatcher.setTenantId(tenantId);

      // Credential callback: invoked by the SDK on every (re)connect for a fresh token.
      // It also refreshes cachedGroups so the `connected` handler always re-joins current groups.
      const getClientAccessUrl = async (): Promise<string> => {
        const { apiInstance: api, config: cfg, tenantId: tid, userId: uid } = this;
        if (!api || !cfg || !tid || !uid) {
          throw new Error('[WebPubSubService] Cannot negotiate access URL: service is disconnected');
        }
        const result = await this.resolveNegotiateResult(cfg, api, tid, uid);
        this.cachedGroups = result.groups;
        return result.url;
      };

      const pubsubClient = new WebPubSubClient({ getClientAccessUrl });

      pubsubClient.on('group-message', (e: unknown) => {
        // Guard: ignore events from a client that has been detached (intentional disconnect
        // or replacement by a new client). The real SDK emits events asynchronously after
        // stop(), so this.client is already null/replaced by the time they arrive.
        if (this.client !== pubsubClient) return;
        // The SDK event `e` may carry the payload as `e.message` (SDK v1+) or
        // directly as `e` (older shape). Extract whichever is present.
        const message = (e as { message?: { data?: unknown } })?.message ?? (e as { data?: unknown });
        this.handleMessage(message as { data: unknown });
      });

      pubsubClient.on('connected', () => {
        if (this.client !== pubsubClient) return;
        this.reconnectAttempts = 0;
        this.setState('connected');
        this.startHeartbeat();
        this.joinGroups(pubsubClient, this.cachedGroups);
      });

      pubsubClient.on('disconnected', () => {
        // Guard: a deliberate disconnect() sets this.client = null before the SDK's
        // async close events arrive, so stale events are silently dropped here.
        if (this.client !== pubsubClient) return;
        this.stopHeartbeat();
        // SDK autoReconnect (default: true) handles reconnection via getClientAccessUrl
        // callback, fetching a fresh token on each attempt.
        this.setState('reconnecting');
      });

      pubsubClient.on('stopped', () => {
        // Guard: same as disconnected — drop events from detached clients so that an
        // intentional disconnect() never triggers a spurious reconnect loop.
        if (this.client !== pubsubClient) return;
        // SDK exhausted its own auto-reconnect retries. Fall back to a manual reconnect
        // that builds a brand-new client (never replays the stale URL/token).
        this.stopHeartbeat();
        this.attemptReconnect();
      });

      this.client = pubsubClient;
      await pubsubClient.start();
    } catch (error) {
      const connectError = error instanceof Error ? error : new Error('Connection failed');
      this.setState('error', connectError);
    }
  }

  disconnect(): void {
    this.stopHeartbeat();
    this.clearReconnectTimer();
    // Null this.client BEFORE calling stop() so the stale-client guard in every event
    // handler suppresses the `disconnected` and `stopped` events that the real SDK
    // emits synchronously inside stop() (via _handleConnectionCloseAndNoRecovery).
    const client = this.client;
    this.client = null;
    this.dispatcher.clear();
    this.reconnectAttempts = 0;
    this.setState('disconnected');
    this.apiInstance = null;
    this.config = null;
    this.tenantId = null;
    this.userId = null;
    client?.stop();
  }

  subscribe(options: SubscribeOptions): string {
    return this.dispatcher.subscribe(options);
  }

  unsubscribe(id: string): void {
    this.dispatcher.unsubscribe(id);
  }

  onConnectionStateChanged(handler: ConnectionStateHandler): () => void {
    this.connectionStateHandlers.add(handler);
    return () => {
      this.connectionStateHandlers.delete(handler);
    };
  }

  // ─── Private ─────────────────────────────────────────────────────────────

  private joinGroups(pubsubClient: { joinGroup: (group: string) => Promise<unknown> }, groups: string[]): void {
    // forEach signals fire-and-forget intent: each promise is individually error-handled via .catch()
    groups.forEach((group) => {
      pubsubClient.joinGroup(group).catch((error: unknown) => {
        console.error(`[WebPubSubService] Failed to join group "${group}":`, error);
      });
    });
  }

  private async resolveNegotiateResult(
    config: RealtimeConfig,
    apiInstance: AxiosInstance,
    tenantId: string,
    userId: string,
  ): Promise<NegotiateResult> {
    if (config.clientUrl) {
      return { groups: [], url: config.clientUrl };
    }

    if (!config.negotiateUrl) {
      throw new Error('[WebPubSubService] Either clientUrl or negotiateUrl must be provided in RealtimeConfig');
    }

    const response = await apiInstance.post<{ groups?: string[]; url: string }>(config.negotiateUrl, {
      tenantId,
      userId,
    });
    return { groups: response.data.groups ?? [], url: response.data.url };
  }

  private handleMessage(message: { data: unknown }): void {
    try {
      const raw = message?.data;
      // The Azure Web PubSub SDK delivers messages in two shapes depending on the
      // dataType field: an already-parsed object (dataType: 'json') or a JSON string
      // (dataType: 'text'). Both cases are handled here before dispatching.
      const parsed: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (parsed !== null && typeof parsed === 'object' && 'type' in parsed) {
        this.dispatcher.dispatch(parsed as PharosRealtimeNotification);
      }
    } catch (error) {
      console.error('[WebPubSubService] Failed to handle message:', error);
    }
  }

  /**
   * Manual fallback reconnect triggered when the SDK's own auto-reconnect exhausts
   * (i.e., the `stopped` event fires). Creates a brand-new client via a full
   * re-negotiate — never replays the stale URL or token. Bounded by maxReconnectAttempts.
   */
  private attemptReconnect(): void {
    const maxAttempts = this.config?.maxReconnectAttempts ?? DEFAULT_MAX_RECONNECT_ATTEMPTS;
    const intervalMs = this.config?.reconnectIntervalMs ?? DEFAULT_RECONNECT_INTERVAL_MS;

    if (this.reconnectAttempts >= maxAttempts) {
      this.setState('error', new Error(`[WebPubSubService] Max reconnect attempts (${maxAttempts}) reached`));
      return;
    }

    this.reconnectAttempts++;
    this.setState('reconnecting');

    const config = this.config;
    const apiInstance = this.apiInstance;
    const tenantId = this.tenantId;
    const userId = this.userId;

    if (!config || !apiInstance || !tenantId || !userId) {
      this.setState('error', new Error('[WebPubSubService] Cannot reconnect: missing connection parameters'));
      return;
    }

    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      // Full re-negotiate + new client — never calls start() on the stale client.
      this.connect(config, apiInstance, tenantId, userId).catch((error: unknown) => {
        console.error('[WebPubSubService] Reconnect failed:', error);
      });
    }, intervalMs);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== undefined) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
  }

  private startHeartbeat(): void {
    const intervalMs = this.config?.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS;
    this.heartbeatTimer = setInterval(() => {
      if (this.state !== 'connected') {
        this.stopHeartbeat();
      }
    }, intervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== undefined) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
    }
  }

  private setState(state: RealtimeConnectionState, error?: Error): void {
    this.state = state;
    this.error = error ?? null;
    for (const handler of this.connectionStateHandlers) {
      handler(state, error);
    }
  }
}
